// ABOUTME: Does this transcript message have anything to show? A message with
// no body, no cards and no live typing indicator renders as a bare timestamp in
// an empty bubble — which is what the owner saw when a stream's placeholder was
// orphaned mid-turn (#2255). Filter those out of the message list rather than
// letting ChatMessage paint them.

import type { AIChatMessage } from '@/stores/aiChatTypes'

/**
 * Metadata keys that carry NO renderable card — pure bookkeeping the transcript
 * never paints. Everything else counts as content.
 *
 * Deliberately a denylist, not an allowlist of card keys: an empty bubble is a
 * cosmetic annoyance, but dropping a card from the transcript is data loss. A
 * new card key is therefore renderable by default, and only a key proven inert
 * belongs here. `posted_confirmation` is the case that makes this matter —
 * `usePublishConfirmationStream` appends `role: 'assistant', content: ''` messages whose
 * entire payload is that card, and a naive `content === ''` filter deletes the
 * confirmation that closes the publish loop.
 */
const INERT_METADATA_KEYS: ReadonlySet<string> = new Set(['backend_message_id'])

/** True when `metadata` holds at least one key ChatMessage may render. */
function hasRenderableMetadata(message: AIChatMessage): boolean {
  const metadata = message.metadata
  if (!metadata) return false
  return Object.entries(metadata).some(
    ([key, value]) => value != null && !INERT_METADATA_KEYS.has(key)
  )
}

/**
 * Whether a message should appear in the transcript at all.
 *
 * Streaming messages always render — the typing indicator IS their content, and
 * hiding the bubble until the first token would make the assistant look dead.
 */
export function shouldRenderMessage(message: AIChatMessage): boolean {
  if (message.content.trim().length > 0) return true
  if (message.isStreaming) return true
  if (message.isError) return true
  if (message.attachments && message.attachments.length > 0) return true
  return hasRenderableMetadata(message)
}
